// Tweened currency display — companion to TweenedNumber but for
// composite multi-tier currency values that get formatted with units
// ("47 gp 3 sp 12 cp" rather than a single integer). TweenedNumber
// can't handle this directly because the formatter returns a string
// with mixed numerals and unit labels, so a naive integer tween
// causes the unit boundaries to flicker on every frame.
//
// Approach: tween the *base integer* (total currency in the lowest-
// denomination units, e.g. copper-equivalents) via TweenedNumber's
// raf-loop, and call the caller-supplied formatter on each rendered
// value. The displayed string updates per frame, but only the
// number portions change; units stay stable.
//
// Usage:
//   <TweenedCurrency
//     baseValue={walletToBase(char.wallet, tiers)}
//     formatter={(base) => formatPriceFromBase(base)}
//   />
//
// Reduced-motion: snaps instantly (no tween). Inherited from
// useReducedMotion via the underlying TweenedNumber-style loop.
//
// MOBILE-REDESIGN.md §11 #2 ("Number tweens, not jumps... the single-
// largest difference between 'spreadsheet' and 'game'") — applies as
// much to wealth as to HP. Buying a 50-gp scroll should *feel* like
// 50 gp leaving your pocket, not a hard snap.

import React, { useEffect, useRef, useState } from 'react';
import useReducedMotion from './useReducedMotion';
import { DUR_BAR } from './transitions';

export default function TweenedCurrency({
  baseValue,
  formatter,
  durationMs = DUR_BAR,
}) {
  const reduceMotion = useReducedMotion();
  const [displayedBase, setDisplayedBase] = useState(baseValue);
  const fromRef = useRef(baseValue);
  const targetRef = useRef(baseValue);
  const startRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (baseValue === targetRef.current) return undefined;
    if (reduceMotion) {
      setDisplayedBase(baseValue);
      targetRef.current = baseValue;
      fromRef.current = baseValue;
      return undefined;
    }
    fromRef.current = displayedBase;
    targetRef.current = baseValue;
    startRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const animate = (now) => {
      const elapsed = now - startRef.current;
      const tp = Math.min(1, elapsed / durationMs);
      // Ease-out cubic — same curve TweenedNumber uses for consistency.
      const eased = 1 - Math.pow(1 - tp, 3);
      const v = fromRef.current + (targetRef.current - fromRef.current) * eased;
      setDisplayedBase(tp === 1 ? targetRef.current : Math.round(v));
      if (tp < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseValue, reduceMotion, durationMs]);

  // Null guard: callers may pass null/undefined when no wallet exists
  // (e.g., a character without a currency tier table). Render nothing
  // rather than passing null through a formatter that may not handle
  // it — keeps the sticky header title slot clean.
  if (displayedBase == null) return null;
  const text = typeof formatter === 'function' ? formatter(displayedBase) : String(displayedBase);
  return <>{text}</>;
}
